import React, { useState } from 'react'

import { useCurrentUser } from '../../../graphql/hooks'
import MobileMenu from '../MobileMenu'
import { ButtonAsLink } from '../../Button'
import { HeaderButton, MenuButton } from './HeaderButton'
import HeaderLogo from './HeaderLogo'

import { HeaderButtonProps } from './HeaderButton/HeaderButton'
import { ButtonAsLinkProps } from '../../Button/Button.types'

import * as styles from './Header.styles'
import { Beta, useBetas } from '../../../utilities/useBeta'
import { inboxNavButtonConfig } from '../../../features/eventInvitation'
import { myTicketsNavButtonConfig } from '../../../features/tickets/ticketRoutes'
import { SelectCityMutatingCompact } from '../../forms/SelectCity/SelectCity'
import {
  OverflowMenu,
  SelectedProfileLink,
} from './ProfileButton/ProfileButton'

export interface NavButtonConfig extends HeaderButtonProps {
  /**Optionally override the rendering of the button */
  element?: React.ReactNode
  mobile?: boolean
}

let sceneButton: NavButtonConfig = {
  label: 'Scene',
  route: '/scene',
  iconName: 'fa fa-stars',
  beta: 'scene',
}

let desktopLoggedInNavItems: NavButtonConfig[] = [
  sceneButton,
  { label: 'Artists', route: '/artists', iconName: 'fa fa-user' },
  { label: 'Genres', route: '/genres', iconName: 'fa fa-music' },
  { label: 'Venues', route: '/venues', iconName: 'fas fa-map-marker-alt' },
  { label: 'Saved', route: '/saved', iconName: 'fas fa-heart' },
  inboxNavButtonConfig,
  myTicketsNavButtonConfig,
]

let desktopLoggedOutNavItems: NavButtonConfig[] = [
  { label: 'Artists', route: '/for-artists' },
  { label: 'Venues', route: '/for-venues' },
  {
    label: 'Promotions',
    route: '/yupty-promotions',
  },
  // {
  //   label: 'Promoters',
  //   route: '/for-promoters',
  // },
  {
    label: 'Pricing',
    route: '/pricing',
  },
].map((item) => ({ ...item, prefetch: 'render' }))

let mobileLoggedInNavItems: NavButtonConfig[] = [
  {
    label: 'Home',
    route: '/',
    element: <HeaderLogo size={35} />,
    iconName: 'fal fa-newspaper',
  },
  sceneButton,
  { label: 'Genres', route: '/genres', iconName: 'fa fa-music' },
  { label: 'Artists', route: '/artists', iconName: 'fa fa-user' },
  { label: 'Venues', route: '/venues', iconName: 'fas fa-map-marker-alt' },
  // { label: 'Saved', route: '/saved', iconName: 'fas fa-heart' },
  inboxNavButtonConfig,
].map((item) => ({ ...item, mobile: true, prefetch: 'render' }))

let loggedOutRightSectionItems: (ButtonAsLinkProps & { label: string })[] = [
  {
    label: 'Log in',
    to: '/login',
    plain: true,
    prefetch: 'render',
  },
]
let mobileMenuLoggedInItems: NavButtonConfig[] = [
  myTicketsNavButtonConfig,
  { label: 'Settings', route: '/settings', iconName: 'fa fa-cog' },
  { label: 'Saved', route: '/saved', iconName: 'fas fa-heart' },
  {
    label: 'Select city',
    route: '/settings/account',
    iconName: 'fa fa-address-card',
  },
].map((item) => ({ ...item, mobile: true, prefetch: 'render' }))

let mobileMenuLoggedOutItems: NavButtonConfig[] = [
  {
    label: 'Artists',
    route: '/for-artists',
    //  iconName: 'fas fa-star'
  },
  {
    label: 'Venues',
    route: '/for-venues',
    //  iconName: 'fa fa-map-marker-alt'
  },
  {
    label: 'Promotions',
    route: '/yupty-promotions',
    // iconName: 'fas fa-drum',
  },

  // {
  //   label: 'Promoters',
  //   route: '/for-promoters',
  //   iconName: 'fas fa-bullhorn',
  // },
  {
    label: 'Pricing',
    route: '/pricing',
    // iconName: 'fas fa-money-bill',
  },
].map((item) => ({ ...item, mobile: true, prefetch: 'render' }))

let Header: React.FC = () => {
  let { selectedProfile, isAuthenticated } = useCurrentUser()

  let navItems = isAuthenticated
    ? [...desktopLoggedInNavItems, ...mobileLoggedInNavItems]
    : [...desktopLoggedOutNavItems, ...mobileMenuLoggedOutItems]
  let navBetaNames = navItems
    .filter((item) => item.beta)
    .map((item) => item.beta) as Beta[]
  let betas = useBetas(navBetaNames)

  let [isMenuExpanded, setIsMenuExpanded] = useState<boolean>(false)
  let toggleMenu = () => {
    setIsMenuExpanded(!isMenuExpanded)
  }
  let hideMenu = () => setIsMenuExpanded(false)

  let navElements = navItems.map((item, i) => {
    let navStyles = item.mobile
      ? [styles.hiddenAboveMedium]
      : [styles.hiddenBelowMedium]

    if (isAuthenticated) {
      navStyles.push(styles.authenticatedHeaderItem)
    }

    let key = item.label + item.iconName + i

    if (item.beta && !betas[item.beta]) {
      return null
    } else if (!item.element) {
      return (
        <div css={navStyles} key={key}>
          <HeaderButton {...item} />
        </div>
      )
    } else if (item.element) {
      return (
        <div css={navStyles} key={key}>
          {item.element}
        </div>
      )
    }

    return null
  })

  let LoggedInContent = () =>
    isAuthenticated && (
      <div
        css={[styles.headerInnerLoggedIn, !isAuthenticated && styles.hidden]}
      >
        <div css={styles.desktopLeftSection}>
          <HeaderLogo size={45} />
          <SelectCityMutatingCompact />
        </div>

        <div css={styles.rightSectionLoggedIn}>
          {navElements}

          <div css={styles.hiddenAboveMedium}>
            <MenuButton
              css={[styles.headerItem]}
              isOpen={isMenuExpanded}
              toggle={toggleMenu}
            />
          </div>

          <div css={styles.hiddenBelowMedium}>
            <OverflowMenu />
          </div>
          <div css={styles.hiddenBelowMedium}>
            {selectedProfile && (
              <SelectedProfileLink
                profile={selectedProfile}
                composeCss={styles.selectedProfileButton}
              />
            )}
          </div>
        </div>
      </div>
    )

  let LoggedOutContent = () =>
    !isAuthenticated && (
      <div css={styles.headerInnerLoggedOut}>
        <HeaderLogo size={45} />

        <div css={styles.leftSection}>{navElements}</div>

        <div css={styles.rightSectionLoggedOut}>
          {loggedOutRightSectionItems.map(({ label, ...item }) => (
            <ButtonAsLink
              key={item.to as string}
              {...item}
              css={styles.rightSectionButton}
            >
              {label}
            </ButtonAsLink>
          ))}
          <div css={styles.hiddenAboveMedium}>
            <MenuButton
              css={styles.headerItem}
              isOpen={isMenuExpanded}
              toggle={toggleMenu}
              hideIcon
            />
          </div>
        </div>
      </div>
    )

  return (
    <>
      {/* <div css={styles.hiddenAboveMedium}>
        <Logo />
      </div> */}
      <section css={styles.headerOuter}>
        <LoggedInContent />
        <LoggedOutContent />

        <MobileMenu
          mobileMenuItems={
            isAuthenticated ? mobileMenuLoggedInItems : mobileMenuLoggedOutItems
          }
          isMenuExpanded={isMenuExpanded}
          hideMenu={hideMenu}
        />
      </section>
    </>
  )
}

export default Header
